import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ZakatProvider } from "./context/ZakatContext";
import { ZakatGlobalProvider } from "./context/ZakatGlobalContext";
import { LiteModeProvider } from "./context/LiteModeContext";
import { BusinessZakatProvider } from "./context/BusinessZakatContext";
import { Toaster } from "./components/ui/sonner";
import LanguagePrompt from "./components/LanguagePrompt";
import LanguageNudgeBanner from "./components/LanguageNudgeBanner";
import PwaInstallBanner from "./components/PwaInstallBanner";
import ScrollToTop from "./components/ScrollToTop";
import { useGAPageviews } from "./hooks/useGAPageviews";

// Eagerly-loaded landing page — first paint must be instant.
import ZakatGlobalPage from "./pages/ZakatGlobalPage";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";

// Route-level code splitting: secondary routes load only when visited.
// Drops the main bundle size significantly (jsPDF + docx + per-page UI
// stay out of the initial chunk).
const InheritancePage    = lazy(() => import("./pages/InheritancePage"));
const WillPage           = lazy(() => import("./pages/WillPage"));
const LearnPage          = lazy(() => import("./pages/LearnPage"));
const LegalPage          = lazy(() => import("./pages/LegalPage"));
const BusinessZakatPage  = lazy(() => import("./pages/BusinessZakatPage"));
const PrivacyPage        = lazy(() => import("./pages/PrivacyPage"));
const WalkthroughPage    = lazy(() => import("./pages/WalkthroughPage"));
const WomensRightsPage   = lazy(() => import("./pages/WomensRightsPage"));
const SuccessionPlanningPage = lazy(() => import("./pages/SuccessionPlanningPage"));
// P0 Homepage restructure — dedicated Zakat sub-pages so the homepage
// itself can stay compact (hero + 3 journey cards + quick answer + FAQ).
const ZakatCalculatorPage = lazy(() => import("./pages/ZakatCalculatorPage"));
// Programmatic SEO guides — country × topic and asset guides.
const GuidesIndexPage       = lazy(() => import("./pages/GuidePages").then(m => ({ default: m.GuidesIndexPage })));
const CountryTopicGuidePage = lazy(() => import("./pages/GuidePages").then(m => ({ default: m.CountryTopicGuidePage })));
const AssetGuidePage        = lazy(() => import("./pages/GuidePages").then(m => ({ default: m.AssetGuidePage })));
// Article routes
const ArticleIndexPage   = lazy(() => import("./pages/ArticleIndexPage"));
const ArticlePage        = lazy(() => import("./pages/ArticlePage"));
// Phase 2 — Life & Planning suite
const LifePage           = lazy(() => import("./pages/LifePage"));
const BudgetPage         = lazy(() => import("./pages/life/BudgetPage"));
const SadaqahPage        = lazy(() => import("./pages/life/SadaqahPage"));
const WaqfPlanPage       = lazy(() => import("./pages/life/WaqfPlanPage"));
const EstatePlanPage     = lazy(() => import("./pages/life/EstatePlanPage"));
const EolChecklistPage   = lazy(() => import("./pages/life/EolChecklistPage"));
const CoursesPage        = lazy(() => import("./pages/life/CoursesPage"));
const MasjidResourcesPage = lazy(() => import("./pages/MasjidResourcesPage"));
const ContactPage        = lazy(() => import("./pages/ContactPage"));
const LandingPage        = lazy(() => import("./pages/LandingPage"));
const PersonalCalculator = lazy(() => import("./pages/PersonalCalculator"));
const BusinessCalculator = lazy(() => import("./pages/BusinessCalculator"));
const ReportPage         = lazy(() => import("./pages/ReportPage"));
const DistributionGuide  = lazy(() => import("./pages/DistributionGuide"));

// Minimal, brand-tinted loading state shown while a chunk is fetched.
// Kept intentionally plain — visible only on slow networks.
const RouteFallback = () => (
  <div
    data-testid="route-loading"
    className="min-h-screen flex items-center justify-center bg-gradient-to-b from-stone-50 to-[#F3EFEA]"
  >
    <div className="flex items-center gap-3 text-[#4A5568]">
      <div className="w-5 h-5 rounded-full border-2 border-[#0D4733] border-t-transparent animate-spin" />
      <span className="text-sm font-mono uppercase tracking-[0.18em]">Loading…</span>
    </div>
  </div>
);

// Inner wrapper so the SPA-route GA tracker has access to <BrowserRouter>
const RoutedApp = () => {
  useGAPageviews();
  return (
    <>
      <ScrollToTop />
      <LanguageNudgeBanner />
      <PwaInstallBanner />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* New ZakatGlobal as main page. `/zakat` is an alias so direct
            navigation and refresh both work — historically the calculator
            lived only at `/`, which caused a blank screen on /zakat load. */}
        <Route path="/" element={
          <RouteErrorBoundary routeName="the Zakat calculator" storageKey="zi_zakat_global_v1">
            <ZakatGlobalPage />
          </RouteErrorBoundary>
        } />
        <Route path="/zakat" element={
          <RouteErrorBoundary routeName="the Zakat calculator" storageKey="zi_zakat_global_v1">
            <ZakatGlobalPage />
          </RouteErrorBoundary>
        } />
        <Route path="/zakat/calculator" element={
          <RouteErrorBoundary routeName="the Zakat calculator" storageKey="zi_zakat_global_v1">
            <ZakatCalculatorPage />
          </RouteErrorBoundary>
        } />
        {/* /zakat/learn merged into /learn — permanent redirect */}
        <Route path="/zakat/learn" element={<Navigate to="/learn" replace />} />
        {/* Programmatic SEO guides — topic × country + asset */}
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/asset/:asset" element={<AssetGuidePage />} />
        <Route path="/guides/:country/:topic" element={<CountryTopicGuidePage />} />
        <Route path="/business-calculator" element={<BusinessZakatPage />} />
        <Route path="/inheritance" element={
          <RouteErrorBoundary routeName="the inheritance calculator" storageKey="zi-inheritance-state-v1">
            <InheritancePage />
          </RouteErrorBoundary>
        } />
        <Route path="/will" element={
          <RouteErrorBoundary routeName="the will writer" storageKey="zi_will_draft_v1">
            <WillPage />
          </RouteErrorBoundary>
        } />
        <Route path="/succession" element={
          <RouteErrorBoundary routeName="the succession planning generator" storageKey={null}>
            <SuccessionPlanningPage />
          </RouteErrorBoundary>
        } />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/:code" element={<LegalPage />} />
        {/* P11 — `/laws` matches the top-nav label "Laws" while `/legal` stays
            the canonical route. Both render the same LegalPage. */}
        <Route path="/laws" element={<LegalPage />} />
        <Route path="/laws/:code" element={<LegalPage />} />
        <Route path="/articles" element={<ArticleIndexPage />} />
        <Route path="/articles/:slug" element={<ArticlePage />} />
        <Route path="/walkthrough" element={<WalkthroughPage />} />
        <Route path="/womens-rights" element={<WomensRightsPage />} />

        {/* Phase 2 — Life & Planning suite */}
        <Route path="/life"            element={<LifePage />} />
        <Route path="/life/budget"     element={<BudgetPage />} />
        <Route path="/life/sadaqah"    element={<SadaqahPage />} />
        <Route path="/life/waqf-plan"  element={<WaqfPlanPage />} />
        <Route path="/life/estate"     element={<EstatePlanPage />} />
        <Route path="/life/eol"        element={<EolChecklistPage />} />
        <Route path="/life/courses"    element={<CoursesPage />} />
        <Route path="/masjid-resources" element={<MasjidResourcesPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Legacy simple calculator routes */}
        <Route path="/simple" element={<LandingPage />} />
        <Route path="/personal" element={<PersonalCalculator />} />
        <Route path="/business" element={<BusinessCalculator />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/guide" element={<DistributionGuide />} />

        {/* Catch-all — any unmatched URL redirects to the homepage instead
            of rendering blank. Prevents the "No routes matched location..."
            warning users saw when hitting /zakat, /laws or a typo. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </>
  );
};

function App() {
  return (
    <ZakatGlobalProvider>
      <LiteModeProvider>
      <ZakatProvider>
        <BusinessZakatProvider>
          <div className="App">
            <BrowserRouter>
              <RoutedApp />
              <LanguagePrompt />
            </BrowserRouter>
            <Toaster position="bottom-right" />
          </div>
        </BusinessZakatProvider>
      </ZakatProvider>
    </LiteModeProvider>
    </ZakatGlobalProvider>
  );
}

export default App;
